import { useState } from "react";
import { faChevronRight, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Button, Form, Modal, Table } from "react-bootstrap";
import MISTooltip from "../../../../components/tooltip";
import type {
    AttendanceManagementSession,
    IAttendanceManagementConfiguration,
    IAttendanceManagementTimingValues,
    IAttendanceManagementYearTimingConfiguration,
} from "../../../../models/AttendanceManagement";

type ManagementTimingKey = "am" | "pm";

interface IManagementSessionTimingScheduleProps {
    configuration: IAttendanceManagementConfiguration;
    isEditing: boolean;
    onChange: (configuration: IAttendanceManagementConfiguration) => void;
}

type ManagementTimingScope =
    | { kind: "default" }
    | { kind: "year"; year: string };

interface IManagementTimingDetail {
    scope: ManagementTimingScope;
    session: AttendanceManagementSession;
}

function toManagementTimingKey(session: AttendanceManagementSession): ManagementTimingKey {
    return session.toLowerCase() as ManagementTimingKey;
}

function formatManagementTime(value: string): string {
    const [hourValue, minuteValue] = value.split(":");
    const hour = Number(hourValue);

    if (!Number.isInteger(hour) || !/^\d{2}:\d{2}$/.test(value)) {
        return value;
    }

    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;

    return `${displayHour}:${minuteValue} ${period}`;
}

function toManagementMinutes(value: string): number | null {
    const [hourValue, minuteValue] = value.split(":");
    const hour = Number(hourValue);
    const minute = Number(minuteValue);

    return Number.isInteger(hour) && Number.isInteger(minute)
        && hour >= 0 && hour < 24 && minute >= 0 && minute < 60
        ? (hour * 60) + minute
        : null;
}

function formatManagementTimeFromMinutes(value: number): string {
    const minutesInDay = 24 * 60;
    const normalisedValue = ((value % minutesInDay) + minutesInDay) % minutesInDay;
    const hour = Math.floor(normalisedValue / 60);
    const minute = normalisedValue % 60;

    return formatManagementTime(`${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`);
}

interface IManagementTimingBandPreviewItem {
    className: "pre-session" | "on-time" | "early-late" | "late-late";
    range: string;
}

function getManagementTimingBandPreviewItems(
    values: IAttendanceManagementTimingValues,
): IManagementTimingBandPreviewItem[] | null {
    const sessionStart = toManagementMinutes(values.sessionStartTime);
    const timingBandValues = [
        values.preSessionGraceMinutes,
        values.onTimeGraceMinutes,
        values.earlyLateBandMinutes,
        values.lateLateBandMinutes,
    ];

    if (sessionStart === null || timingBandValues.some((value) => !Number.isInteger(value) || value < 0)) {
        return null;
    }

    const onTimeEnd = sessionStart + values.onTimeGraceMinutes;
    const earlyLateEnd = onTimeEnd + values.earlyLateBandMinutes;
    const lateLateEnd = earlyLateEnd + values.lateLateBandMinutes;

    return [
        {
            className: "pre-session",
            range: values.preSessionGraceMinutes === 0
                ? "No early arrival window"
                : `${formatManagementTimeFromMinutes(
                    sessionStart - values.preSessionGraceMinutes,
                )} - ${formatManagementTimeFromMinutes(sessionStart)}`,
        },
        {
            className: "on-time",
            range: `${formatManagementTimeFromMinutes(sessionStart)} - ${formatManagementTimeFromMinutes(onTimeEnd)}`,
        },
        {
            className: "early-late",
            range: `${formatManagementTimeFromMinutes(onTimeEnd)} - ${formatManagementTimeFromMinutes(earlyLateEnd)}`,
        },
        {
            className: "late-late",
            range: `${formatManagementTimeFromMinutes(earlyLateEnd)} - ${formatManagementTimeFromMinutes(lateLateEnd)}`,
        },
    ];
}

function managementSessionLabel(session: AttendanceManagementSession): string {
    return `${session} Registration`;
}

function cloneManagementTimingValues(
    values: IAttendanceManagementTimingValues,
): IAttendanceManagementTimingValues {
    return { ...values };
}

function cloneManagementConfiguration(
    configuration: IAttendanceManagementConfiguration,
): IAttendanceManagementConfiguration {
    return {
        ...configuration,
        availableYears: [...configuration.availableYears],
        defaultTimingConfiguration: configuration.defaultTimingConfiguration
            ? {
                am: cloneManagementTimingValues(configuration.defaultTimingConfiguration.am),
                pm: cloneManagementTimingValues(configuration.defaultTimingConfiguration.pm),
            }
            : null,
        timingConfigurations: configuration.timingConfigurations.map((timing) => ({
            ...timing,
            am: {
                ...timing.am,
                values: timing.am.values ? cloneManagementTimingValues(timing.am.values) : null,
            },
            pm: {
                ...timing.pm,
                values: timing.pm.values ? cloneManagementTimingValues(timing.pm.values) : null,
            },
        })),
        codeCatalogue: configuration.codeCatalogue.map((entry) => ({ ...entry })),
        protectedMarks: [...configuration.protectedMarks],
        overwritableMarks: [...configuration.overwritableMarks],
        capabilities: { ...configuration.capabilities },
    };
}

function getManagementYearTiming(
    configuration: IAttendanceManagementConfiguration,
    year: string,
): IAttendanceManagementYearTimingConfiguration | undefined {
    return configuration.timingConfigurations.find((timing) => timing.year === year);
}

function createInheritedManagementYearTiming(
    year: string,
): IAttendanceManagementYearTimingConfiguration {
    return {
        year,
        am: { usesEstablishmentDefault: true, values: null },
        pm: { usesEstablishmentDefault: true, values: null },
    };
}

function getManagementTimingValues(
    configuration: IAttendanceManagementConfiguration,
    scope: ManagementTimingScope,
    session: AttendanceManagementSession,
): IAttendanceManagementTimingValues | null {
    const sessionKey = toManagementTimingKey(session);

    if (scope.kind === "default") {
        return configuration.defaultTimingConfiguration?.[sessionKey] ?? null;
    }

    const timing = getManagementYearTiming(configuration, scope.year);
    const yearSession = timing?.[sessionKey];

    if (!yearSession) {
        return null;
    }

    return yearSession.usesEstablishmentDefault
        ? configuration.defaultTimingConfiguration?.[sessionKey] ?? null
        : yearSession.values;
}

function isManagementTimingInherited(
    configuration: IAttendanceManagementConfiguration,
    scope: ManagementTimingScope,
    session: AttendanceManagementSession,
): boolean {
    if (scope.kind === "default") {
        return false;
    }

    return getManagementYearTiming(configuration, scope.year)?.[toManagementTimingKey(session)]
        .usesEstablishmentDefault ?? false;
}

function updateManagementDefaultTiming(
    configuration: IAttendanceManagementConfiguration,
    session: AttendanceManagementSession,
    updates: Partial<IAttendanceManagementTimingValues>,
): IAttendanceManagementConfiguration {
    if (!configuration.defaultTimingConfiguration) {
        return configuration;
    }

    const sessionKey = toManagementTimingKey(session);

    return {
        ...configuration,
        defaultTimingConfiguration: {
            ...configuration.defaultTimingConfiguration,
            [sessionKey]: {
                ...configuration.defaultTimingConfiguration[sessionKey],
                ...updates,
            },
        },
    };
}

function updateManagementYearTiming(
    configuration: IAttendanceManagementConfiguration,
    year: string,
    session: AttendanceManagementSession,
    updates: Partial<IAttendanceManagementTimingValues>,
): IAttendanceManagementConfiguration {
    const sessionKey = toManagementTimingKey(session);

    return {
        ...configuration,
        timingConfigurations: configuration.timingConfigurations.map((timing) => {
            const currentSession = timing[sessionKey];

            if (timing.year !== year || currentSession.usesEstablishmentDefault || !currentSession.values) {
                return timing;
            }

            return {
                ...timing,
                [sessionKey]: {
                    ...currentSession,
                    values: {
                        ...currentSession.values,
                        ...updates,
                    },
                },
            };
        }),
    };
}

function updateManagementYearSessionSource(
    configuration: IAttendanceManagementConfiguration,
    year: string,
    session: AttendanceManagementSession,
    usesEstablishmentDefault: boolean,
): IAttendanceManagementConfiguration {
    if (usesEstablishmentDefault && !configuration.defaultTimingConfiguration) {
        return configuration;
    }

    const sessionKey = toManagementTimingKey(session);

    return {
        ...configuration,
        timingConfigurations: configuration.timingConfigurations.map((timing) => {
            if (timing.year !== year) {
                return timing;
            }

            const currentSession = timing[sessionKey];
            const nextValues = usesEstablishmentDefault
                ? null
                : currentSession.values
                    ? cloneManagementTimingValues(currentSession.values)
                    : configuration.defaultTimingConfiguration
                        ? cloneManagementTimingValues(configuration.defaultTimingConfiguration[sessionKey])
                        : null;

            if (!usesEstablishmentDefault && !nextValues) {
                return timing;
            }

            return {
                ...timing,
                [sessionKey]: {
                    usesEstablishmentDefault,
                    values: nextValues,
                },
            };
        }),
    };
}

function getManagementOrderedYears(
    configuration: IAttendanceManagementConfiguration,
): string[] {
    const unexpectedConfiguredYears = configuration.timingConfigurations
        .map((timing) => timing.year)
        .filter((year) => !configuration.availableYears.includes(year));

    return [...configuration.availableYears, ...unexpectedConfiguredYears];
}

function ManagementTimingValueFields(props: {
    disabled: boolean;
    onChange: (updates: Partial<IAttendanceManagementTimingValues>) => void;
    session: AttendanceManagementSession;
    values: IAttendanceManagementTimingValues;
}) {
    const fieldPrefix = `attendance-timing-${props.session.toLowerCase()}`;
    const labelPrefix = props.session;
    const timingBandPreviewItems = getManagementTimingBandPreviewItems(props.values);
    const numericFields: Array<{
        field: "preSessionGraceMinutes" | "onTimeGraceMinutes" | "earlyLateBandMinutes" | "lateLateBandMinutes";
        label: string;
        tooltip: string;
    }> = [
        {
            field: "preSessionGraceMinutes",
            label: "Pre-Session Grace",
            tooltip: "Minutes before registration start that count in the pre-session arrival window.",
        },
        {
            field: "onTimeGraceMinutes",
            label: "On-Time Grace",
            tooltip: "Minutes after registration start during which an arrival remains on time.",
        },
        {
            field: "earlyLateBandMinutes",
            label: "Early-Late Band",
            tooltip: "Minutes after the on-time grace during which an arrival is classified as early late.",
        },
        {
            field: "lateLateBandMinutes",
            label: "Late-Late Band",
            tooltip: "Minutes after the early-late band during which an arrival is classified as late late.",
        },
    ];

    return (
        <>
            <div className="attendance-session-editor-fields">
                <div className="attendance-session-editor-field">
                    {props.disabled ? (
                        <div className="form-label">Registration Start</div>
                    ) : (
                        <Form.Label htmlFor={`${fieldPrefix}-start`}>Registration Start</Form.Label>
                    )}
                    {props.disabled ? (
                        <div className="attendance-session-static-value" data-testid={`${fieldPrefix}-start`}>
                            {formatManagementTime(props.values.sessionStartTime)}
                        </div>
                    ) : (
                        <Form.Control
                            aria-label={`${labelPrefix} session start time`}
                            data-testid={`${fieldPrefix}-start`}
                            id={`${fieldPrefix}-start`}
                            onChange={(event) => props.onChange({ sessionStartTime: event.target.value })}
                            type="time"
                            value={props.values.sessionStartTime}
                        />
                    )}
                </div>
                <div className="attendance-session-editor-field">
                    {props.disabled ? (
                        <div className="form-label">Session End</div>
                    ) : (
                        <Form.Label htmlFor={`${fieldPrefix}-end`}>Session End</Form.Label>
                    )}
                    {props.disabled ? (
                        <div className="attendance-session-static-value" data-testid={`${fieldPrefix}-end`}>
                            {formatManagementTime(props.values.sessionEndTime)}
                        </div>
                    ) : (
                        <Form.Control
                            aria-label={`${labelPrefix} session end time`}
                            data-testid={`${fieldPrefix}-end`}
                            id={`${fieldPrefix}-end`}
                            onChange={(event) => props.onChange({ sessionEndTime: event.target.value })}
                            type="time"
                            value={props.values.sessionEndTime}
                        />
                    )}
                </div>
            </div>
            <section aria-labelledby={`${fieldPrefix}-arrival-timing`} className="attendance-registration-timing">
                <div className="attendance-registration-timing-header">
                    <h4 id={`${fieldPrefix}-arrival-timing`}>Arrival Timing</h4>
                </div>
                <div className="attendance-timing-band-controls">
                    {numericFields.map(({ field, label, tooltip }) => (
                        <div className="attendance-timing-band-preview" key={field}>
                            <div className="attendance-timing-band-label">
                                {props.disabled ? (
                                    <div className="form-label">{label}</div>
                                ) : (
                                    <Form.Label htmlFor={`${fieldPrefix}-${field}`}>{label}</Form.Label>
                                )}
                                <MISTooltip message={tooltip} placement="top">
                                    <button
                                        aria-label={`About ${label}`}
                                        className="attendance-timing-band-help"
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faCircleInfo} />
                                    </button>
                                </MISTooltip>
                            </div>
                            {props.disabled ? (
                                <div className="attendance-session-static-value" data-testid={`${fieldPrefix}-${field}`}>
                                    {`${props.values[field]} min`}
                                </div>
                            ) : (
                                <Form.Control
                                    aria-label={`${labelPrefix} ${label.toLowerCase()}`}
                                    data-testid={`${fieldPrefix}-${field}`}
                                    id={`${fieldPrefix}-${field}`}
                                    min="0"
                                    onChange={(event) => props.onChange({
                                        [field]: event.target.value.length === 0 ? 0 : Number(event.target.value),
                                    })}
                                    type="number"
                                    value={props.values[field]}
                                />
                            )}
                        </div>
                    ))}
                </div>
                {timingBandPreviewItems && (
                    <div aria-label="Arrival timing windows" className="attendance-timing-band-timeline">
                        {timingBandPreviewItems.map((item) => (
                            <div className={`attendance-timing-band is-${item.className}`} key={item.className}>
                                <span>{item.range}</span>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}

function ManagementSessionTimingEditor(props: {
    configuration: IAttendanceManagementConfiguration;
    detail: IManagementTimingDetail;
    isEditing: boolean;
    onChange: (configuration: IAttendanceManagementConfiguration) => void;
    onSelectSession: (session: AttendanceManagementSession) => void;
}) {
    const { configuration, detail, isEditing } = props;
    const sessionKey = toManagementTimingKey(detail.session);
    const year = detail.scope.kind === "year" ? detail.scope.year : null;
    const yearSession = year
        ? getManagementYearTiming(configuration, year)?.[sessionKey]
        : null;
    const inherited = isManagementTimingInherited(configuration, detail.scope, detail.session);
    const values = getManagementTimingValues(configuration, detail.scope, detail.session);
    const sourceLabel = detail.scope.kind === "default"
        ? "Establishment default"
        : inherited
            ? "Uses establishment default"
            : "Configured year timing";

    const updateValues = (updates: Partial<IAttendanceManagementTimingValues>) => {
        if (detail.scope.kind === "default") {
            props.onChange(updateManagementDefaultTiming(configuration, detail.session, updates));
            return;
        }

        if (year) {
            props.onChange(updateManagementYearTiming(configuration, year, detail.session, updates));
        }
    };

    return (
        <div className="attendance-session-editor-layout">
            <nav aria-label="Registration sessions" className="attendance-session-picker">
                <span className="attendance-session-picker-heading">Sessions</span>
                {(["AM", "PM"] as AttendanceManagementSession[]).map((session) => {
                    const isSelected = detail.session === session;

                    return (
                        <button
                            aria-current={isSelected ? "page" : undefined}
                            className={`attendance-session-picker-option ${isSelected ? "is-selected" : ""}`}
                            data-testid={`attendance-timing-session-${session.toLowerCase()}`}
                            key={session}
                            onClick={() => props.onSelectSession(session)}
                            type="button"
                        >
                            <span className="attendance-session-picker-copy">
                                <strong>{managementSessionLabel(session)}</strong>
                            </span>
                            <FontAwesomeIcon className="attendance-session-picker-chevron" icon={faChevronRight} />
                        </button>
                    );
                })}
            </nav>
            <section aria-label={`${managementSessionLabel(detail.session)} timing`} className="attendance-session-editor-panel">
                <header className="attendance-session-editor-header">
                    <div>
                        <h3>{managementSessionLabel(detail.session)}</h3>
                        <p>{sourceLabel}</p>
                    </div>
                    {year && isEditing && yearSession && (
                        <div
                            aria-label={`${managementSessionLabel(detail.session)} timing source`}
                            className="attendance-inheritance-control"
                            role="group"
                        >
                            {configuration.defaultTimingConfiguration && (
                                <button
                                    aria-pressed={inherited}
                                    className={`attendance-inheritance-option ${inherited ? "is-selected" : ""}`}
                                    data-testid={`attendance-timing-${detail.session.toLowerCase()}-use-default`}
                                    onClick={() => props.onChange(updateManagementYearSessionSource(
                                        configuration,
                                        year,
                                        detail.session,
                                        true,
                                    ))}
                                    type="button"
                                >
                                    Use Default
                                </button>
                            )}
                            <button
                                aria-pressed={!inherited}
                                className={`attendance-inheritance-option ${!inherited ? "is-selected" : ""}`}
                                data-testid={`attendance-timing-${detail.session.toLowerCase()}-use-explicit`}
                                onClick={() => props.onChange(updateManagementYearSessionSource(
                                    configuration,
                                    year,
                                    detail.session,
                                    false,
                                ))}
                                type="button"
                            >
                                Use Configured Timing
                            </button>
                        </div>
                    )}
                </header>
                {values ? (
                    <ManagementTimingValueFields
                        disabled={!isEditing || inherited}
                        onChange={updateValues}
                        session={detail.session}
                        values={values}
                    />
                ) : (
                    <div className="attendance-monitor-empty" role="status">
                        Timing values are not available for this session.
                    </div>
                )}
            </section>
        </div>
    );
}

function ManagementTimingHeader(props: {
    configuration: IAttendanceManagementConfiguration;
    isEditing: boolean;
    onOpenDefault: () => void;
}) {
    const defaultTiming = props.configuration.defaultTimingConfiguration;

    return (
        <header aria-labelledby="attendance-section-heading" className="attendance-schedule-header">
            <div>
                <h2 id="attendance-section-heading">Session Timing</h2>
                <p>Configure establishment and year timing for AM and PM registrations.</p>
            </div>
            <div aria-label="Default schedule" className="attendance-default-schedule-summary">
                <span className="attendance-default-schedule-label">Default Schedule</span>
                {defaultTiming ? (
                    <div className="attendance-default-schedule-values">
                        {(["AM", "PM"] as AttendanceManagementSession[]).map((session) => {
                            const sessionKey = toManagementTimingKey(session);

                            return (
                                <span className="attendance-default-schedule-session" key={session}>
                                    <strong>{session}</strong>
                                    <strong>{formatManagementTime(defaultTiming[sessionKey].sessionStartTime)}</strong>
                                    <span>{formatManagementTime(defaultTiming[sessionKey].sessionEndTime)}</span>
                                </span>
                            );
                        })}
                    </div>
                ) : (
                    <span className="attendance-registration-not-configured">No establishment default</span>
                )}
            </div>
            {defaultTiming && (
                <div className="attendance-default-schedule-actions">
                    <Button
                        aria-label={`${props.isEditing ? "Edit" : "View"} Default Schedule`}
                        data-testid="attendance-timing-default-action"
                        onClick={props.onOpenDefault}
                        size="sm"
                        variant="outline-primary"
                    >
                        {props.isEditing ? "Edit Default" : "View Default"}
                    </Button>
                </div>
            )}
        </header>
    );
}

function ManagementYearTimingTable(props: {
    configuration: IAttendanceManagementConfiguration;
    isEditing: boolean;
    onOpen: (year: string) => void;
}) {
    const orderedYears = getManagementOrderedYears(props.configuration);

    return (
        <section aria-label="Year group timing" className="attendance-year-group-overrides">
            <Table responsive className={`attendance-data-table attendance-year-groups-table mb-0 ${props.isEditing ? "is-editing" : ""}`}>
                <colgroup>
                    <col className="attendance-year-group-column" />
                    <col className="attendance-registration-column" />
                    <col className="attendance-registration-column" />
                    <col className="attendance-source-column" />
                    <col className="attendance-source-column" />
                    <col className="attendance-action-column" />
                </colgroup>
                <thead>
                    <tr>
                        <th scope="col">Year Group</th>
                        <th scope="col">AM Registration</th>
                        <th scope="col">PM Registration</th>
                        <th scope="col">AM Source</th>
                        <th scope="col">PM Source</th>
                        <th scope="col">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {orderedYears.length === 0 ? (
                        <tr>
                            <td className="attendance-monitor-empty" colSpan={6}>No year group timing is available.</td>
                        </tr>
                    ) : orderedYears.map((year) => {
                        const timing = getManagementYearTiming(props.configuration, year);
                        const amValues = timing
                            ? getManagementTimingValues(props.configuration, { kind: "year", year }, "AM")
                            : null;
                        const pmValues = timing
                            ? getManagementTimingValues(props.configuration, { kind: "year", year }, "PM")
                            : null;
                        const canOpen = Boolean(timing || props.configuration.defaultTimingConfiguration);
                        const yearIdentifier = year.replace(/\s+/g, "-").toLowerCase();

                        return (
                            <tr key={year}>
                                <td><strong>{year}</strong></td>
                                <td>{amValues ? formatManagementTime(amValues.sessionStartTime) : "Not configured"}</td>
                                <td>{pmValues ? formatManagementTime(pmValues.sessionStartTime) : "Not configured"}</td>
                                <td>
                                    {timing ? (
                                        <Badge className={`attendance-source-badge ${timing.am.usesEstablishmentDefault ? "is-default" : "is-override"}`}>
                                            {timing.am.usesEstablishmentDefault ? "Default" : "Configured"}
                                        </Badge>
                                    ) : "Not configured"}
                                </td>
                                <td>
                                    {timing ? (
                                        <Badge className={`attendance-source-badge ${timing.pm.usesEstablishmentDefault ? "is-default" : "is-override"}`}>
                                            {timing.pm.usesEstablishmentDefault ? "Default" : "Configured"}
                                        </Badge>
                                    ) : "Not configured"}
                                </td>
                                <td>
                                    <Button
                                        aria-label={`${props.isEditing ? "Edit" : "View"} ${year} timing`}
                                        data-testid={`attendance-timing-year-${yearIdentifier}-action`}
                                        disabled={!canOpen}
                                        onClick={() => props.onOpen(year)}
                                        size="sm"
                                        variant="outline-primary"
                                    >
                                        {props.isEditing ? "Edit" : "View"}
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </section>
    );
}

export default function SessionTimingSchedule(props: IManagementSessionTimingScheduleProps) {
    const [detail, setDetail] = useState<IManagementTimingDetail | null>(null);
    const [draft, setDraft] = useState<IAttendanceManagementConfiguration | null>(null);

    const openDefault = () => {
        if (!props.configuration.defaultTimingConfiguration) {
            return;
        }

        setDraft(cloneManagementConfiguration(props.configuration));
        setDetail({ scope: { kind: "default" }, session: "AM" });
    };

    const openYear = (year: string) => {
        const timing = getManagementYearTiming(props.configuration, year);

        if (!timing && !props.configuration.defaultTimingConfiguration) {
            return;
        }

        const nextDraft = cloneManagementConfiguration(props.configuration);

        if (!timing) {
            nextDraft.timingConfigurations.push(createInheritedManagementYearTiming(year));
        }

        setDraft(nextDraft);
        setDetail({ scope: { kind: "year", year }, session: "AM" });
    };

    const closeModal = () => {
        setDetail(null);
        setDraft(null);
    };

    const applyDraft = () => {
        if (draft) {
            props.onChange(draft);
        }

        closeModal();
    };

    return (
        <div className="attendance-session-schedule">
            <ManagementTimingHeader
                configuration={props.configuration}
                isEditing={props.isEditing}
                onOpenDefault={openDefault}
            />
            <ManagementYearTimingTable
                configuration={props.configuration}
                isEditing={props.isEditing}
                onOpen={openYear}
            />
            <Modal
                backdrop="static"
                centered
                contentClassName="attendance-session-detail-modal"
                dialogClassName={`attendance-session-detail-dialog${detail?.scope.kind === "default" ? " attendance-default-session-detail-dialog" : ""}`}
                onHide={closeModal}
                show={detail !== null && draft !== null}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {detail?.scope.kind === "default"
                            ? "Establishment Schedule"
                            : detail?.scope.kind === "year"
                                ? `${detail.scope.year} Session Timing`
                                : "Session Timing"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detail && draft && (
                        <ManagementSessionTimingEditor
                            configuration={draft}
                            detail={detail}
                            isEditing={props.isEditing}
                            onChange={setDraft}
                            onSelectSession={(session) => setDetail({ ...detail, session })}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {props.isEditing ? (
                        <>
                            <Button data-testid="attendance-timing-cancel" onClick={closeModal} variant="outline-secondary">Cancel</Button>
                            <Button data-testid="attendance-timing-done" onClick={applyDraft} variant="primary">Done</Button>
                        </>
                    ) : (
                        <Button onClick={closeModal} variant="outline-secondary">Close</Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
}